import client from "./client";

const API_URL = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace("/auth", "/bots")
  : "http://localhost:5000/api/bots";

export const createBot = async (name) => {
  try {
    const response = await client.post(API_URL, { name });
    return response.data;
  } catch (error) {
    const data = error.response?.data;
    if (error.response?.status === 403 && data?.upgrade) {
      const err = new Error(data.message);
      err.upgrade = true;
      throw err;
    }
    throw new Error(data?.message || "Failed to create bot");
  }
};

export const getBots = async () => {
  try {
    const response = await client.get(API_URL);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch bots";
  }
};

export const getBotById = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch bot";
  }
};

export const proxyFetch = async ({ url, method, headers, body, timeoutMs }) => {
  const PROXY_URL = process.env.REACT_APP_AUTH_API
    ? process.env.REACT_APP_AUTH_API.replace("/auth", "/proxy")
    : "http://localhost:5000/api/proxy";
  try {
    const response = await client.post(
      `${PROXY_URL}/fetch`,
      { url, method, headers, body, timeoutMs },
      { timeout: (timeoutMs || 10000) + 2000 }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: error.response?.data?.error || error.message || "proxy fetch failed" };
  }
};

export const updateBot = async (botId, payload) => {
  try {
    const response = await client.put(`${API_URL}/${botId}`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to update bot";
  }
};

export const deleteBot = async (botId) => {
  try {
    const response = await client.delete(`${API_URL}/${botId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete bot";
  }
};

export const getBotWhitelist = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/whitelist`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch whitelist";
  }
};

export const getBotLanguage = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/language`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch language settings";
  }
};

export const saveBotLanguage = async (botId, language, prefStatement) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/language`, { language, prefStatement });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to save language settings";
  }
};

export const saveBotWhitelist = async (botId, domains) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/whitelist`, { domains });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to save whitelist";
  }
};

export const listTemplates = async () => {
  try {
    const response = await client.get(`${API_URL}/templates`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to list templates";
  }
};

export const generateHelpCenter = async (botId, { topic, count } = {}) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/helpcenter/generate`, { topic, count });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to generate articles";
  }
};

export const generateQuestions = async (botId, { prompt, count } = {}) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/questions/generate`, { prompt, count });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to generate questions";
  }
};

export const getKnowledgeDocuments = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/knowledge`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch documents";
  }
};

export const uploadKnowledgeDocument = async (botId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await client.post(`${API_URL}/${botId}/knowledge/upload`, formData);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to upload document";
  }
};

export const deleteKnowledgeDocument = async (botId, docName) => {
  try {
    const response = await client.delete(`${API_URL}/${botId}/knowledge/${encodeURIComponent(docName)}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to delete document";
  }
};

export const getKnowledgeDocumentChunks = async (botId, docName) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/knowledge/${encodeURIComponent(docName)}`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to fetch chunks";
  }
};

export const startCrawl = async (botId, url, maxPages = 50) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/crawl/start`, { url, maxPages });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to start crawl";
  }
};

export const getCrawlStatus = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/crawl/status`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to get crawl status";
  }
};

export const stopCrawl = async (botId) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/crawl/stop`, {});
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to stop crawl";
  }
};

export const getCrawlQueueStatus = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/crawl/queue-status`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to get queue status";
  }
};

export const getBotCrawlInfo = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/crawl/info`);
    return response.data;
  } catch (error) {
    return { lastUrl: null, lastCrawledAt: null, pageCount: 0 };
  }
};

export const getCrawledPages = async (botId) => {
  try {
    const response = await client.get(`${API_URL}/${botId}/crawl/pages`);
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to get crawled pages";
  }
};

export const applyTemplate = async (botId, templateKey) => {
  try {
    const response = await client.post(`${API_URL}/${botId}/apply-template`, { templateKey });
    return response.data;
  } catch (error) {
    throw error.response?.data?.message || "Failed to apply template";
  }
};
